import axios from 'axios';
import prettyBytes from 'pretty-bytes';
import { CanceledError } from '../../../components/CancelBuild';
import { getErrorMessage } from '../../../utilities/CliError';
import { currentUser } from '../../../utilities/firestore';
import getCallableFirebaseFunction from '../../../utilities/getCallableFirebaseFunction';
import logForCI from '../../../utilities/logForCI';
import logger from '../../../utilities/logger';
import postToFirebaseFunction from '../../../utilities/postToFirebaseFunction';
import getProjectConfig from '../../../utilities/projectConfig/getProjectConfig';
import shouldExitOnBuildFailure from '../../../utilities/shouldExitOnBuildFailure';
import getAppPkgJson from './getPackageJson';
import getVersionControlInfo from './getVersionControlInfo';
import spyBuild from './spyBuild';
import { BuildFlags, BuildState } from './types';
import uploadApplicationSource from './uploadApplicationSource';

const deleteBuildFn = getCallableFirebaseFunction('deleteBuild');
const preflightSigningConfigFn = getCallableFirebaseFunction(
  'preflightSigningConfig',
);

export default async function runBuild({
  configPath,
  flags,
  signal,
  updateState,
}: {
  configPath?: string;
  flags: BuildFlags;
  signal?: AbortSignal;
  updateState: (state: Partial<BuildState>) => void;
}) {
  logForCI('Getting application information...');

  if (flags.breakpointPlan) {
    updateState({ breakpointPlan: flags.breakpointPlan });
  }
  const primaryUserId = currentUser()?.uid;

  // This will throw if validation fails or project root can not be found:
  const { config, unprocessedConfig } = getProjectConfig(configPath, {
    build: flags,
  });
  const appId = config.id;
  const appPkgJson = getAppPkgJson({ config });

  // Start signing preflight in the background; use a non-rejecting pattern
  const signingPreflightPromise =
    flags.shouldCodeSign !== false
      ? preflightSigningConfigFn({ appId }).catch((e: unknown) => ({
          data: {
            status: 'fail',
            checks: [
              {
                platform: 'windows',
                name: 'Code Signing',
                status: 'fail',
                error: `Signing preflight call failed: ${getErrorMessage(e)}`,
              },
            ],
          },
        }))
      : undefined;

  const buildObserver = spyBuild();
  buildObserver.onUpdate(({ build }) => updateState({ build }));

  updateState({
    appId,
    appPkg: appPkgJson as { name: string; version: string },
    onUnmount: () => buildObserver.unsubscribe(),
    preparationStageLabel: 'Preparing',
    preparationProgress: 0.02,
    state: 'preparing',
  });
  logForCI('Preparing...');

  // Create build in Firebase
  let buildId: string;
  signal?.throwIfAborted();
  try {
    if (!config.appPath) {
      throw new Error('Project config is missing appPath');
    }

    const prepareResult = await postToFirebaseFunction(
      'prepareNewBuild',
      {
        appPkgName: appPkgJson.name,
        appPkgProductName: appPkgJson.productName,
        appVersion: appPkgJson.version,
        id: config.id,
        onBuildFinishedWebhook: flags.onBuildFinishedWebhook,
        projectConfig: unprocessedConfig,
        shouldCodeSign: flags.shouldCodeSign !== false,
        shouldRelease: false,
        userId: primaryUserId,
        versionControlInfo: await getVersionControlInfo(config.appPath),
        introspect: flags.introspect,
        breakpoints: flags.breakpointPlan?.breakpoints,
        idToken: await currentUser()?.getIdToken(),
      },
      { signal },
    );
    buildId = prepareResult.appData.meta.currentBuildProgress.id;

    signal?.throwIfAborted();
    buildObserver.subscribe({ appId, buildId, userId: prepareResult.userId });
    await buildObserver.whenFirstUpdate();
  } catch (e) {
    throw addErrorMessage(e, 'Failed while preparing new build');
  }

  logForCI(`Retrieved build ID: ${buildId}`);
  updateState({
    preparationProgress: 0.05,
    preparationStageLabel: 'Uploading',
  });
  logForCI('Uploading...');

  let sourceArchiveDetails: unknown;
  signal?.throwIfAborted();
  try {
    sourceArchiveDetails = await uploadApplicationSource({
      appId,
      appPkgJson,
      buildId,
      config,
      onProgress(progress: number, uploadedSize?: number) {
        // Upload is treated as 95% of preparation
        const preparationState = {
          preparationProgress: 0.05 + (progress / 100) * 0.95,
          preparationUploadedSize: '',
        };

        if (uploadedSize) {
          preparationState.preparationUploadedSize = prettyBytes(uploadedSize);
        }

        updateState(preparationState);
      },
    });
  } catch (e) {
    await tryDeleteBuild(appId, buildId);
    throw addErrorMessage(e, 'Failed while uploading application source');
  }

  updateState({ state: 'building' });
  logForCI('Kicking off build...');

  // Wait for signing preflight before dispatching the remote build
  if (signingPreflightPromise) {
    const { data: preflight } = await signingPreflightPromise;
    if (preflight.status === 'fail') {
      await tryDeleteBuild(appId, buildId);
      throw new Error(
        `Signing preflight failed; ${formatPreflightError(preflight)}`,
      );
    }
  }

  signal?.throwIfAborted();
  try {
    await postToFirebaseFunction(
      'kickOffBuild',
      {
        appBuilderLibVersion: config.appBuilderLibVersion,
        appId,
        appPkgName: appPkgJson.name,
        appVersion: appPkgJson.version,
        buildId,
        idToken: await currentUser()?.getIdToken(),
        linuxImageVersion: config.linux?.imageVersion,
        nodeVersion: config.nodeVersion,
        npmVersion: config.npmVersion,
        pnpmVersion: config.pnpmVersion,
        sourceArchiveDetails,
        userId: primaryUserId,
      },
      { signal },
    );
  } catch (e) {
    throw addErrorMessage(e, 'Failed while kicking off build');
  }

  if (flags.exitAfterUploading) {
    updateState({ state: 'exit-after-uploading' });
    return;
  }

  signal?.throwIfAborted();
  const build = await buildObserver.whenSettled();
  if (build?.status === 'failed' && shouldExitOnBuildFailure(build)) {
    updateState({ state: 'build-failed' });
  }
}

function addErrorMessage(e: unknown, message: string): Error {
  if (CanceledError.isCanceled(e)) {
    return e;
  }

  let originalMessage = '';
  if (axios.isAxiosError(e) && e.response?.data?.message) {
    originalMessage = e.response.data.message;
  } else {
    originalMessage = getErrorMessage(e);
  }

  const newError = new Error(`${message}; ${originalMessage}`);
  if (e instanceof Error) {
    newError.stack = e.stack;
  }

  return newError;
}

function formatPreflightError(preflight: {
  checks: {
    platform: string;
    name: string;
    certName?: string;
    status: string;
    error?: string;
  }[];
}): string {
  const parts = preflight.checks
    .filter((c) => c.status === 'fail' && c.error)
    .map((c) =>
      c.certName
        ? `${c.platform} cert "${c.certName}": ${c.error}`
        : `${c.platform} ${c.name}: ${c.error}`,
    );

  return parts.join('; ') || 'Unknown signing configuration issue';
}

async function tryDeleteBuild(appId: string, buildId: string) {
  try {
    await deleteBuildFn({ appId, buildId });
  } catch (e) {
    logger.error({ err: e }, 'Failed to delete build after error');
  }
}
